// Notes on a renewal (spec C7.4, S14): the words and the calls behind the Notes card, the Add note
// popup and the toast with Undo.
import type { Api, Note } from '../auth/api';
import { stampTime } from './fetch-lines';

/** The longest note the portal keeps (the server says the same). */
export const noteMax = 2000;

/** The text a note keeps, or null while there is nothing to add. */
export const noteReady = (typed: string) => {
  const t = typed.trim();
  return t.length > 0 && t.length <= noteMax ? t : null;
};

/** "Example Reviewer, Nov 12 14:20". */
export const noteByline = (n: Note) => `${n.by.name}, ${stampTime(n.at)}`;

/** What the toast's Undo points at. */
export type NoteUndo = { renewalId: string; noteId: string };

/** Adds the note: the toast and its Undo, or the line to show under the box. */
export async function saveNote(
  api: Api,
  renewalId: string,
  text: string,
): Promise<{ ok: true; toast: string; undo: NoteUndo } | { ok: false; error: string }> {
  const res = await api.addNote(renewalId, text);
  if (res.ok) {
    return { ok: true, toast: 'Note added.', undo: { renewalId, noteId: res.body.note.id } };
  }
  return {
    ok: false,
    error:
      res.error === 'deleted'
        ? 'This record was deleted. Restore it to add notes.'
        : 'Could not add the note. Try again.',
  };
}

/** The toast's Undo: takes the note back, and says how it went. */
export async function takeBackNote(api: Api, u: NoteUndo): Promise<string> {
  const res = await api.undoNote(u.renewalId, u.noteId);
  return res.ok ? 'Undone. The note is taken back.' : 'Could not undo. The note stays.';
}
